import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Workflow, Columns3, CalendarCheck,
  Inbox, Sprout, Settings, LogOut, Sparkles, Clock, Linkedin, Search, BarChart3, Shield, Eye, Send, Handshake, Target,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import RunMyDayFAB from "./RunMyDayFAB";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/sprint", label: "Sprint", icon: Target, testid: "nav-sprint" },
  { to: "/leads", label: "Leads", icon: Users, testid: "nav-leads" },
  { to: "/bulk-preview", label: "Bulk Preview", icon: Eye, testid: "nav-bulk-preview" },
  { to: "/linkedin-auto", label: "LinkedIn Auto", icon: Linkedin, testid: "nav-linkedin-auto" },
  { to: "/lead-finder", label: "Lead Finder", icon: Search, testid: "nav-lead-finder" },
  { to: "/pipeline", label: "Pipeline", icon: Columns3, testid: "nav-pipeline" },
  { to: "/sequences", label: "Sequences", icon: Workflow, testid: "nav-sequences" },
  { to: "/enrollments", label: "Enrollments", icon: Clock, testid: "nav-enrollments" },
  { to: "/linkedin", label: "LinkedIn", icon: Linkedin, testid: "nav-linkedin" },
  { to: "/warm-intros", label: "Warm Intros", icon: Handshake, testid: "nav-warm-intros" },
  { to: "/triage", label: "Inbox", icon: Inbox, testid: "nav-triage" },
  { to: "/outreach", label: "Outreach", icon: Send, testid: "nav-outreach" },
  { to: "/meetings", label: "Meetings", icon: CalendarCheck, testid: "nav-meetings" },
  { to: "/nurture", label: "Nurture", icon: Sprout, testid: "nav-nurture" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" },
  { to: "/deliverability", label: "Deliverability", icon: Shield, testid: "nav-deliverability" },
  { to: "/go-live", label: "Go-Live", icon: Sparkles, testid: "nav-go-live" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

export default function Layout({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="w-[260px] shrink-0 border-r border-border bg-secondary/40 flex flex-col" data-testid="sidebar">
        <div className="px-6 py-6 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 bg-primary flex items-center justify-center" style={{ borderRadius: 2 }}>
              <Sparkles className="h-4 w-4 text-primary-foreground" strokeWidth={1.8} />
            </div>
            <div>
              <div className="font-serif text-lg leading-none tracking-tight" style={{ fontWeight: 500 }}>GT Engine</div>
              <div className="overline mt-1">CFO Appointments</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1">
          {navItems.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 text-sm transition-all ${
                  isActive
                    ? "bg-card border border-border accent-left text-foreground font-medium"
                    : "text-muted-foreground hover:text-foreground hover:bg-card/60 border border-transparent"
                }`
              }
              style={{ borderRadius: 2 }}
            >
              <Icon className="h-4 w-4" strokeWidth={1.5} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-border p-3">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-8 w-8 bg-foreground text-background flex items-center justify-center text-xs font-semibold" style={{ borderRadius: 2 }}>
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate" data-testid="sidebar-user-name">{user?.name}</div>
              <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="text-muted-foreground hover:text-foreground transition-colors"
              title="Logout"
            >
              <LogOut className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        <header className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-10">
          <div className="px-8 py-5 flex items-end justify-between gap-4">
            <div>
              {subtitle && <div className="overline mb-1">{subtitle}</div>}
              <h1 className="font-serif tracking-tight text-3xl lg:text-4xl" style={{ fontWeight: 400 }} data-testid="page-title">
                {title}
              </h1>
            </div>
            {actions && <div className="flex items-center gap-2">{actions}</div>}
          </div>
        </header>
        <div className="flex-1 px-8 py-8">{children}</div>
      </main>
      <RunMyDayFAB />
    </div>
  );
}
